import { defineStore, acceptHMRUpdate } from 'pinia'
import type { UserInfo, UserInfoResponse } from '@/models/user_info'

function userInfoFailureMessage(data: UserInfoResponse): string {
  const raw = data.error_message
  if (typeof raw === 'string' && raw.trim().length > 0) {
    return raw.trim()
  }
  return 'Token exchange failed'
}

/**
 * Authenticated user profile from POST `/auth/userinfo` (BFF).
 *
 * State is kept in memory only: session credentials live in HTTP-only cookies (XAUTH-DATA, etc.).
 * Do not persist this store to localStorage — it contains PII.
 */
export const useUserStore = defineStore('user', {
  state: (): { userInfo: UserInfo | null } => ({
    userInfo: null,
  }),
  getters: {
    /** True after a completed exchange with non-null `user_info` (see BFF contract). */
    isLoggedInProfileReady: (state): boolean => state.userInfo != null,
  },
  actions: {
    /**
     * Replace profile from a BFF `UserInfoResponse`. Does not merge with stale fragments.
     * @throws Error when `success` is false or `user_info` is null/undefined.
     */
    setUserInfoFromUserinfoResponse(data: UserInfoResponse) {
      if (!data.success || data.user_info == null) {
        throw new Error(userInfoFailureMessage(data))
      }
      this.userInfo = data.user_info
    },
    /**
     * Replace the in-memory profile from a bare {@link UserInfo} payload (e.g. `GET /auth/me`).
     * Use {@link setUserInfoFromUserinfoResponse} instead when the source is the wrapped
     * `/auth/userinfo` response that carries `success` / `error_*` fields.
     */
    setUserInfo(userInfo: UserInfo) {
      this.userInfo = userInfo
    },
    clearUserInfo() {
      this.userInfo = null
    },
  },
})

// In dev, Vite replaces this module when you save edits. Without `acceptHMRUpdate`, Pinia would
// treat the hot swap as a second store registration (warnings / broken updates). This hook
// applies the new getters and actions to the existing store instance instead of a full reload.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
